import React, { useEffect, useState } from 'react';
import api from '../api';
import { Clock, Power, RefreshCw, CheckCircle2, AlertTriangle, Network } from 'lucide-react';

export default function NtpLocal() {
  const [st, setSt]     = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg]   = useState(null);
  const [me, setMe]     = useState(null);

  const load = () => api.get('/ntp-override').then(r => setSt(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get('/me').then(r => setMe(r.data.user)).catch(() => {});
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const isAdmin = me?.role === 'admin' || me?.canAll;
  const toast = (kind, text) => { setMsg({ kind, text }); setTimeout(() => setMsg(null), 5000); };

  const toggle = async () => {
    const next = !st.enabled;
    if (!window.confirm(next
      ? 'LIGAR o NTP local? As redes dos clientes passam a receber o IP do seu NTP ao consultar os nomes abaixo.'
      : 'DESLIGAR o NTP local? Os clientes voltam a receber o IP real do NTP.br (efeito conforme o TTL/cache dos aparelhos).')) return;
    setBusy(true);
    try {
      await api.post('/ntp-override', { enabled: next });
      await load();
      toast('ok', next ? '✓ NTP local LIGADO' : '✓ NTP local DESLIGADO — DNS normal');
    } catch (e) { toast('err', '✗ ' + (e.response?.data?.error || 'Erro')); }
    finally { setBusy(false); }
  };

  if (!st) return <div className="dim">Carregando…</div>;
  const on = st.enabled;
  const ntpOk = st.container === 'running';

  return (
    <div className="stack-lg">
      {msg && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 9999, maxWidth: 480,
          padding: '12px 16px', borderRadius: 'var(--radius)',
          background: msg.kind === 'ok' ? 'var(--ok-dim)' : 'var(--err-dim)',
          border: '1px solid ' + (msg.kind === 'ok' ? 'rgba(63,185,80,.3)' : 'rgba(248,81,73,.3)'),
          color: msg.kind === 'ok' ? 'var(--ok)' : 'var(--err)', fontSize: 13, fontWeight: 500,
        }}>{msg.text}</div>
      )}

      <header className="row-between">
        <div>
          <h1 className="h1">NTP Local</h1>
          <p className="dim" style={{ fontSize: 14, marginTop: 4 }}>
            Servidor NTP próprio (chrony). Quando <strong>ligado</strong>, o DNS (ns1/ns2) responde o IP deste servidor
            para os nomes abaixo, <strong>somente</strong> para as redes dos clientes. Qualquer outra rede recebe a resposta normal.
          </p>
        </div>
        <button className="btn btn-secondary" onClick={load} disabled={busy}><RefreshCw size={13}/> Atualizar</button>
      </header>

      <div className="card" style={{ borderColor: on ? 'rgba(63,185,80,.4)' : 'var(--border)' }}>
        <div className="hrow" style={{ padding: '18px 20px', gap: 18 }}>
          <div style={{ width: 46, height: 46, borderRadius: 12, background: on ? 'var(--ok-dim)' : 'var(--accent-dim)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Clock size={24} color={on ? 'var(--ok)' : 'var(--accent)'}/>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>
              Override de DNS: <span className={'badge ' + (on ? 'ok' : 'muted')}>{on ? 'LIGADO' : 'DESLIGADO'}</span>
            </div>
            <div className="dim" style={{ fontSize: 12.5, marginTop: 4 }}>
              {on
                ? <>Clientes consultando os nomes abaixo estão sendo direcionados para <code>{st.ntp_ip}</code>
                    {st.rewrites5m !== null && <> · <strong>{st.rewrites5m}</strong> consultas redirecionadas nos últimos 5 min</>}.</>
                : 'DNS normal: os clientes recebem o IP real do NTP.br.'}
            </div>
          </div>
          {isAdmin && (
            <button className={'btn ' + (on ? 'btn-secondary' : 'btn-primary')} onClick={toggle} disabled={busy || (!on && !ntpOk)}>
              <Power size={14}/> {busy ? 'Aplicando…' : (on ? 'Desligar' : 'Ligar')}
            </button>
          )}
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-header"><Clock size={15} color="var(--accent)"/><h3 className="h3">Servidor NTP</h3></div>
          <div className="card-body stack">
            <div className="hrow" style={{ gap: 8 }}>
              {ntpOk ? <CheckCircle2 size={15} color="var(--ok)"/> : <AlertTriangle size={15} color="var(--err)"/>}
              <span>Container <code>tguth_ntp</code>: <strong>{st.container}</strong></span>
            </div>
            <div className="dim" style={{ fontSize: 12.5 }}>Escuta em <code>{st.ntp_ip}:123/udp</code></div>
            <div className="dim" style={{ fontSize: 12.5 }}>Fonte atual: <code>{st.source || '–'}</code></div>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><Network size={15} color="var(--accent)"/><h3 className="h3">Escopo do override</h3></div>
          <div className="card-body stack">
            <div>
              <div className="dim" style={{ fontSize: 12, marginBottom: 4 }}>Redes de clientes</div>
              {st.clients.map(c => <div key={c} className="mono" style={{ fontSize: 13, fontWeight: 600 }}>{c}</div>)}
            </div>
            <div>
              <div className="dim" style={{ fontSize: 12, marginBottom: 4 }}>Nomes redirecionados</div>
              {st.names.map(n => <div key={n} className="mono" style={{ fontSize: 13, fontWeight: 600 }}>{n}</div>)}
            </div>
            <p className="dim" style={{ fontSize: 12, lineHeight: 1.6 }}>
              Listas em <code>config/ntp_override/clients.conf</code> e <code>names.conf</code>.
              Depois de editar, rode <code>scripts/ntp_override.sh apply</code>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
